import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FinancialConfig } from './FinancialConfig.js';
import SMSF_RATES_CONFIG from './SMSF_Rates_Config.js';
import { formatInputValue, getNumericInputProps } from './utils/inputHelpers.js';

const SMSFInvestmentLimitsMonitor = ({ smsfState, setSmsfState }) => {
    const [activeTab, setActiveTab] = useState('dashboard');
    
    // Investment data
    const [investmentData, setInvestmentData] = useState({
        totalAssets: 1000000,
        inHouseAssets: 30000,
        relatedPartyLoans: 20000,
        lrbaProperties: [
            {
                id: 1,
                name: 'Investment Property 1',
                propertyValue: 500000,
                loanAmount: 350000,
                propertyType: 'residential'
            }
        ],
        singleAssetExposure: [
            { asset: 'BHP', value: 150000 },
            { asset: 'CBA', value: 120000 },
            { asset: 'CSL', value: 100000 }
        ],
        collectibles: 0,
        cryptoAssets: 0,
        offshoreAssets: 200000
    });

    const [limits, setLimits] = useState(null);
    const [alerts, setAlerts] = useState([]);

    // Calculate all limits and compliance
    const calculateLimits = () => {
        const totalAssets = investmentData.totalAssets;
        const inHouseAssets = investmentData.inHouseAssets + investmentData.relatedPartyLoans;
        
        // In-House Asset Limit (5%)
        const inHouseLimit = FinancialConfig.SMSF.COMPLIANCE.IN_HOUSE_ASSETS.MAXIMUM_PERCENTAGE;
        const inHouseMaxValue = totalAssets * inHouseLimit;
        const inHousePercentage = (inHouseAssets / totalAssets) * 100;
        const inHouseCompliant = inHouseAssets <= inHouseMaxValue;
        const inHouseUtilization = (inHouseAssets / inHouseMaxValue) * 100;

        // LRBA Limits
        const lrbaResults = investmentData.lrbaProperties.map(property => {
            const lvr = (property.loanAmount / property.propertyValue) * 100;
            const maxLVR = FinancialConfig.SMSF.BORROWING.LRBA.MAXIMUM_LOAN_TO_VALUE * 100;
            const compliant = lvr <= maxLVR;
            const availableBorrowing = (property.propertyValue * FinancialConfig.SMSF.BORROWING.LRBA.MAXIMUM_LOAN_TO_VALUE) - property.loanAmount;
            
            return {
                ...property,
                lvr,
                maxLVR,
                compliant,
                availableBorrowing,
                equity: property.propertyValue - property.loanAmount,
                utilizationPercent: (lvr / maxLVR) * 100
            };
        });

        const totalLRBADebt = lrbaResults.reduce((sum, p) => sum + p.loanAmount, 0);
        const totalLRBAValue = lrbaResults.reduce((sum, p) => sum + p.propertyValue, 0);
        const totalLRBAEquity = lrbaResults.reduce((sum, p) => sum + p.equity, 0);
        const overallLVR = totalLRBAValue > 0 ? (totalLRBADebt / totalLRBAValue) * 100 : 0;

        // Single Asset Concentration
        const largestAsset = investmentData.singleAssetExposure.reduce((max, asset) => 
            asset.value > max.value ? asset : max, { value: 0 });
        const concentrationPercent = (largestAsset.value / totalAssets) * 100;
        const concentrationLimit = 20; // Prudential limit (not regulatory, but best practice)
        const concentrationCompliant = concentrationPercent <= concentrationLimit;

        // Collectibles Limit (Generally not allowed, or very restricted)
        const collectiblesPercent = (investmentData.collectibles / totalAssets) * 100;
        const collectiblesCompliant = investmentData.collectibles === 0; // Safest approach

        // Total Borrowing Capacity
        const maxAdditionalBorrowing = lrbaResults.reduce((sum, p) => sum + Math.max(0, p.availableBorrowing), 0);
        const totalBorrowingPower = totalLRBADebt + maxAdditionalBorrowing;

        // Diversification Analysis
        const diversificationScore = calculateDiversificationScore(investmentData);

        const calculatedLimits = {
            inHouse: {
                current: inHouseAssets,
                limit: inHouseMaxValue,
                percentage: inHousePercentage,
                compliant: inHouseCompliant,
                utilization: inHouseUtilization,
                available: inHouseMaxValue - inHouseAssets,
                regulatory: true,
                severity: inHousePercentage > 5 ? 'error' : inHousePercentage > 4 ? 'warning' : 'ok'
            },
            lrba: {
                properties: lrbaResults,
                totalDebt: totalLRBADebt,
                totalValue: totalLRBAValue,
                totalEquity: totalLRBAEquity,
                overallLVR,
                maxAdditionalBorrowing,
                totalBorrowingPower,
                regulatory: true,
                compliant: lrbaResults.every(p => p.compliant),
                severity: lrbaResults.some(p => !p.compliant) ? 'error' : overallLVR > 70 ? 'warning' : 'ok'
            },
            concentration: {
                largestAsset,
                percentage: concentrationPercent,
                limit: concentrationLimit,
                compliant: concentrationCompliant,
                regulatory: false,
                prudential: true,
                severity: concentrationPercent > 25 ? 'warning' : 'ok'
            },
            collectibles: {
                value: investmentData.collectibles,
                percentage: collectiblesPercent,
                compliant: collectiblesCompliant,
                regulatory: true,
                severity: !collectiblesCompliant ? 'error' : 'ok'
            },
            diversification: diversificationScore
        };

        // Generate alerts
        generateAlerts(calculatedLimits);

        setLimits(calculatedLimits);
    };

    const calculateDiversificationScore = (data) => {
        const scores = {
            assetClasses: 0,
            geographicDiversity: 0,
            singleAssetRisk: 0,
            lrbaConcentration: 0
        };

        // Asset class diversity (simplistic - would need more data in real implementation)
        const hasEquities = data.singleAssetExposure.length > 0;
        const hasProperty = data.lrbaProperties.length > 0;
        const hasOffshore = data.offshoreAssets > 0;
        scores.assetClasses = (hasEquities ? 33 : 0) + (hasProperty ? 33 : 0) + (hasOffshore ? 34 : 0);

        // Geographic (if offshore > 0)
        scores.geographicDiversity = data.offshoreAssets > 0 ? 100 : 0;

        // Single asset risk
        const largestAsset = data.singleAssetExposure.reduce((max, asset) => 
            asset.value > max.value ? asset : max, { value: 0 });
        const concentration = (largestAsset.value / data.totalAssets) * 100;
        scores.singleAssetRisk = concentration < 10 ? 100 : concentration < 20 ? 75 : concentration < 30 ? 50 : 25;

        // LRBA concentration
        const lrbaTotal = data.lrbaProperties.reduce((sum, p) => sum + p.propertyValue, 0);
        const lrbaPercent = (lrbaTotal / data.totalAssets) * 100;
        scores.lrbaConcentration = lrbaPercent < 40 ? 100 : lrbaPercent < 60 ? 75 : lrbaPercent < 80 ? 50 : 25;

        const overallScore = (scores.assetClasses + scores.geographicDiversity + scores.singleAssetRisk + scores.lrbaConcentration) / 4;

        return {
            ...scores,
            overall: overallScore,
            rating: overallScore >= 80 ? 'Excellent' : overallScore >= 60 ? 'Good' : overallScore >= 40 ? 'Fair' : 'Poor'
        };
    };

    const generateAlerts = (calculatedLimits) => {
        const newAlerts = [];

        // In-house asset alerts
        if (!calculatedLimits.inHouse.compliant) {
            newAlerts.push({
                type: 'error',
                category: 'In-House Assets',
                message: `In-house assets (${calculatedLimits.inHouse.percentage.toFixed(2)}%) exceed 5% limit`,
                action: `Reduce by $${(calculatedLimits.inHouse.current - calculatedLimits.inHouse.limit).toLocaleString()}`,
                regulatory: true
            });
        } else if (calculatedLimits.inHouse.utilization > 80) {
            newAlerts.push({
                type: 'warning',
                category: 'In-House Assets',
                message: `In-house assets at ${calculatedLimits.inHouse.utilization.toFixed(0)}% of limit`,
                action: 'Monitor closely, approaching regulatory limit',
                regulatory: true
            });
        }

        // LRBA alerts
        calculatedLimits.lrba.properties.forEach(property => {
            if (!property.compliant) {
                newAlerts.push({
                    type: 'error',
                    category: 'LRBA',
                    message: `${property.name}: LVR ${property.lvr.toFixed(1)}% exceeds ${property.maxLVR}% limit`,
                    action: `Reduce loan by $${(property.loanAmount - (property.propertyValue * FinancialConfig.SMSF.BORROWING.LRBA.MAXIMUM_LOAN_TO_VALUE)).toLocaleString()}`,
                    regulatory: true
                });
            } else if (property.utilizationPercent > 90) {
                newAlerts.push({
                    type: 'warning',
                    category: 'LRBA',
                    message: `${property.name}: LVR at ${property.utilizationPercent.toFixed(0)}% of limit`,
                    action: 'Consider reducing loan or increasing equity',
                    regulatory: false
                });
            }
        });

        // Concentration alerts
        if (!calculatedLimits.concentration.compliant) {
            newAlerts.push({
                type: 'warning',
                category: 'Concentration Risk',
                message: `Single asset (${calculatedLimits.concentration.largestAsset.asset}) represents ${calculatedLimits.concentration.percentage.toFixed(1)}% of portfolio`,
                action: 'Consider diversifying to reduce concentration risk',
                regulatory: false,
                prudential: true
            });
        }

        // Collectibles alerts
        if (!calculatedLimits.collectibles.compliant) {
            newAlerts.push({
                type: 'error',
                category: 'Collectibles',
                message: 'Collectibles investment may not be compliant',
                action: 'Review ATO guidelines on collectibles in SMSFs',
                regulatory: true
            });
        }

        // Diversification alerts
        if (calculatedLimits.diversification.overall < 50) {
            newAlerts.push({
                type: 'warning',
                category: 'Diversification',
                message: `Portfolio diversification score: ${calculatedLimits.diversification.rating}`,
                action: 'Consider improving asset allocation and diversification',
                regulatory: false,
                prudential: true
            });
        }

        setAlerts(newAlerts);
    };

    useEffect(() => {
        calculateLimits();
    }, [investmentData]);

    const renderDashboard = () => {
        if (!limits) return <div>Calculating...</div>;

        return (
            <div className="space-y-6">
                {/* Alerts Summary */}
                {alerts.length > 0 && (
                    <div className="bg-white p-6 rounded-lg shadow">
                        <h3 className="font-semibold text-lg mb-4">🚨 Alerts & Warnings</h3>
                        <div className="space-y-3">
                            {alerts.map((alert, index) => (
                                <div 
                                    key={index}
                                    className={`border-l-4 p-4 rounded ${
                                        alert.type === 'error' ? 'bg-red-50 border-red-500' :
                                        'bg-yellow-50 border-yellow-500'
                                    }`}
                                >
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <span className={`font-semibold ${
                                                alert.type === 'error' ? 'text-red-900' : 'text-yellow-900'
                                            }`}>
                                                {alert.category}
                                                {alert.regulatory && ' (Regulatory)'}
                                                {alert.prudential && ' (Prudential)'}
                                            </span>
                                        </div>
                                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                                            alert.type === 'error' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                                        }`}>
                                            {alert.type.toUpperCase()}
                                        </span>
                                    </div>
                                    <p className={`text-sm mb-2 ${
                                        alert.type === 'error' ? 'text-red-800' : 'text-yellow-800'
                                    }`}>
                                        {alert.message}
                                    </p>
                                    <p className={`text-xs ${
                                        alert.type === 'error' ? 'text-red-700' : 'text-yellow-700'
                                    }`}>
                                        → {alert.action}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Compliance Status Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* In-House Assets */}
                    <div className={`bg-white p-6 rounded-lg shadow border-l-4 ${
                        limits.inHouse.severity === 'error' ? 'border-red-500' :
                        limits.inHouse.severity === 'warning' ? 'border-yellow-500' :
                        'border-green-500'
                    }`}>
                        <div className="text-sm text-gray-600 mb-1">In-House Assets</div>
                        <div className="text-2xl font-bold mb-2">
                            {limits.inHouse.percentage.toFixed(2)}%
                        </div>
                        <div className="flex justify-between items-center text-xs">
                            <span className="text-gray-500">Limit: 5%</span>
                            <span className={`font-medium ${
                                limits.inHouse.compliant ? 'text-green-600' : 'text-red-600'
                            }`}>
                                {limits.inHouse.compliant ? '✓ Compliant' : '✗ Breach'}
                            </span>
                        </div>
                        <div className="mt-3 bg-gray-200 rounded-full h-2">
                            <div 
                                className={`h-2 rounded-full ${
                                    limits.inHouse.severity === 'error' ? 'bg-red-500' :
                                    limits.inHouse.severity === 'warning' ? 'bg-yellow-500' :
                                    'bg-green-500'
                                }`}
                                style={{ width: `${Math.min(limits.inHouse.utilization, 100)}%` }}
                            />
                        </div>
                    </div>

                    {/* LRBA Overall */}
                    <div className={`bg-white p-6 rounded-lg shadow border-l-4 ${
                        limits.lrba.severity === 'error' ? 'border-red-500' :
                        limits.lrba.severity === 'warning' ? 'border-yellow-500' :
                        'border-green-500'
                    }`}>
                        <div className="text-sm text-gray-600 mb-1">Overall LVR</div>
                        <div className="text-2xl font-bold mb-2">
                            {limits.lrba.overallLVR.toFixed(1)}%
                        </div>
                        <div className="flex justify-between items-center text-xs">
                            <span className="text-gray-500">Limit: 80%</span>
                            <span className={`font-medium ${
                                limits.lrba.compliant ? 'text-green-600' : 'text-red-600'
                            }`}>
                                {limits.lrba.compliant ? '✓ Compliant' : '✗ Breach'}
                            </span>
                        </div>
                        <div className="mt-3 bg-gray-200 rounded-full h-2">
                            <div 
                                className={`h-2 rounded-full ${
                                    limits.lrba.severity === 'error' ? 'bg-red-500' :
                                    limits.lrba.severity === 'warning' ? 'bg-yellow-500' :
                                    'bg-green-500'
                                }`}
                                style={{ width: `${(limits.lrba.overallLVR / 80) * 100}%` }}
                            />
                        </div>
                    </div>

                    {/* Borrowing Capacity */}
                    <div className="bg-white p-6 rounded-lg shadow border-l-4 border-blue-500">
                        <div className="text-sm text-gray-600 mb-1">Available Borrowing</div>
                        <div className="text-2xl font-bold text-blue-600 mb-2">
                            ${(limits.lrba.maxAdditionalBorrowing / 1000).toFixed(0)}K
                        </div>
                        <div className="text-xs text-gray-500">
                            Total Power: ${(limits.lrba.totalBorrowingPower / 1000).toFixed(0)}K
                        </div>
                    </div>

                    {/* Diversification */}
                    <div className="bg-white p-6 rounded-lg shadow border-l-4 border-purple-500">
                        <div className="text-sm text-gray-600 mb-1">Diversification</div>
                        <div className="text-2xl font-bold text-purple-600 mb-2">
                            {limits.diversification.rating}
                        </div>
                        <div className="text-xs text-gray-500">
                            Score: {limits.diversification.overall.toFixed(0)}/100
                        </div>
                    </div>
                </div>

                {/* Detailed Breakdowns */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* In-House Assets Detail */}
                    <div className="bg-white p-6 rounded-lg shadow">
                        <h4 className="font-semibold mb-4">In-House Asset Breakdown</h4>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center pb-2 border-b">
                                <span className="text-gray-700">Direct In-House Assets</span>
                                <span className="font-semibold">${investmentData.inHouseAssets.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between items-center pb-2 border-b">
                                <span className="text-gray-700">Related Party Loans</span>
                                <span className="font-semibold">${investmentData.relatedPartyLoans.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between items-center pb-2 border-b font-semibold">
                                <span>Total In-House</span>
                                <span>${limits.inHouse.current.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between items-center text-green-600">
                                <span>Available Capacity</span>
                                <span className="font-semibold">${limits.inHouse.available.toLocaleString()}</span>
                            </div>
                        </div>
                    </div>

                    {/* LRBA Properties Detail */}
                    <div className="bg-white p-6 rounded-lg shadow">
                        <h4 className="font-semibold mb-4">LRBA Properties</h4>
                        <div className="space-y-3">
                            {limits.lrba.properties.map(property => (
                                <div key={property.id} className="border rounded p-3">
                                    <div className="font-medium mb-2">{property.name}</div>
                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                        <div>
                                            <span className="text-gray-600">Value:</span>
                                            <span className="ml-2 font-semibold">${property.propertyValue.toLocaleString()}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Loan:</span>
                                            <span className="ml-2 font-semibold">${property.loanAmount.toLocaleString()}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">LVR:</span>
                                            <span className={`ml-2 font-semibold ${
                                                property.compliant ? 'text-green-600' : 'text-red-600'
                                            }`}>
                                                {property.lvr.toFixed(1)}%
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Can Borrow:</span>
                                            <span className="ml-2 font-semibold text-blue-600">
                                                ${property.availableBorrowing.toLocaleString()}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Concentration Risk */}
                <div className="bg-white p-6 rounded-lg shadow">
                    <h4 className="font-semibold mb-4">Asset Concentration Analysis</h4>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b">
                                    <th className="text-left py-2">Asset</th>
                                    <th className="text-right py-2">Value</th>
                                    <th className="text-right py-2">% of Portfolio</th>
                                    <th className="text-center py-2">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {investmentData.singleAssetExposure
                                    .sort((a, b) => b.value - a.value)
                                    .map((asset, index) => {
                                        const percent = (asset.value / investmentData.totalAssets) * 100;
                                        return (
                                            <tr key={index} className="border-b hover:bg-gray-50">
                                                <td className="py-3 font-medium">{asset.asset}</td>
                                                <td className="text-right py-3">${asset.value.toLocaleString()}</td>
                                                <td className="text-right py-3">{percent.toFixed(1)}%</td>
                                                <td className="text-center py-3">
                                                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                        percent > 25 ? 'bg-red-100 text-red-800' :
                                                        percent > 20 ? 'bg-yellow-100 text-yellow-800' :
                                                        'bg-green-100 text-green-800'
                                                    }`}>
                                                        {percent > 25 ? 'High' : percent > 20 ? 'Elevated' : 'OK'}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    const renderInputs = () => (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-semibold text-lg mb-4">Fund Overview</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Total Assets
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={investmentData.totalAssets}
                            onChange={(e) => setInvestmentData({
                                ...investmentData,
                                totalAssets: Number(e.target.value)
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Offshore Assets
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={investmentData.offshoreAssets}
                            onChange={(e) => setInvestmentData({
                                ...investmentData,
                                offshoreAssets: Number(e.target.value)
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-semibold text-lg mb-4">In-House Assets</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Direct In-House Assets
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={investmentData.inHouseAssets}
                            onChange={(e) => setInvestmentData({
                                ...investmentData,
                                inHouseAssets: Number(e.target.value)
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Related Party Loans
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={investmentData.relatedPartyLoans}
                            onChange={(e) => setInvestmentData({
                                ...investmentData,
                                relatedPartyLoans: Number(e.target.value)
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-semibold text-lg mb-4">LRBA Properties</h3>
                {investmentData.lrbaProperties.map((property, index) => (
                    <div key={property.id} className="border rounded p-4 mb-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Property Value
                                </label>
                                <input
                                    {...getNumericInputProps()}
                                    value={property.propertyValue}
                                    onChange={(e) => {
                                        const updated = [...investmentData.lrbaProperties];
                                        updated[index].propertyValue = Number(e.target.value);
                                        setInvestmentData({ ...investmentData, lrbaProperties: updated });
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Loan Amount
                                </label>
                                <input
                                    {...getNumericInputProps()}
                                    value={property.loanAmount}
                                    onChange={(e) => {
                                        const updated = [...investmentData.lrbaProperties];
                                        updated[index].loanAmount = Number(e.target.value);
                                        setInvestmentData({ ...investmentData, lrbaProperties: updated });
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    const renderEducation = () => (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-semibold text-lg mb-4">📚 Investment Limits Guide</h3>
                
                <div className="space-y-6">
                    <div>
                        <h4 className="font-semibold text-gray-900 mb-2">In-House Assets (5% Limit)</h4>
                        <p className="text-gray-700 mb-2">
                            In-house assets are investments in related parties of the SMSF. Limited to 5% of total fund assets.
                        </p>
                        <div className="bg-blue-50 p-3 rounded">
                            <p className="text-sm text-blue-800"><strong>Examples:</strong></p>
                            <ul className="list-disc list-inside text-sm text-blue-700 mt-1">
                                <li>Loans to members or relatives</li>
                                <li>Investments in related companies</li>
                                <li>Lease arrangements with related parties</li>
                            </ul>
                        </div>
                    </div>

                    <div>
                        <h4 className="font-semibold text-gray-900 mb-2">LRBA (80% LVR Limit)</h4>
                        <p className="text-gray-700 mb-2">
                            Limited Recourse Borrowing Arrangements allow SMSFs to borrow, typically capped at 80% LVR by lenders.
                        </p>
                        <div className="bg-green-50 p-3 rounded">
                            <p className="text-sm text-green-800"><strong>Key Rules:</strong></p>
                            <ul className="list-disc list-inside text-sm text-green-700 mt-1">
                                <li>Single acquirable asset only</li>
                                <li>Limited recourse to fund assets</li>
                                <li>Arm's length terms required</li>
                                <li>Generally 80% maximum LVR from lenders</li>
                            </ul>
                        </div>
                    </div>

                    <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Concentration Risk (Prudential)</h4>
                        <p className="text-gray-700 mb-2">
                            While not a regulatory limit, best practice suggests no single asset should exceed 20% of portfolio.
                        </p>
                        <div className="bg-yellow-50 p-3 rounded">
                            <p className="text-sm text-yellow-800"><strong>Diversification Benefits:</strong></p>
                            <ul className="list-disc list-inside text-sm text-yellow-700 mt-1">
                                <li>Reduces single-asset risk</li>
                                <li>Smooths returns over time</li>
                                <li>Protects retirement savings</li>
                                <li>Improves risk-adjusted returns</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="max-w-7xl mx-auto p-6">
            {/* Header */}
            <div className="mb-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Investment Limits Monitor</h2>
                <p className="text-gray-600">
                    Real-time tracking of regulatory limits and borrowing capacity
                </p>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 mb-6">
                <nav className="flex space-x-8">
                    {[
                        { id: 'dashboard', label: 'Dashboard', icon: '📊' },
                        { id: 'inputs', label: 'Update Data', icon: '✏️' },
                        { id: 'education', label: 'Limits Guide', icon: '📚' }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`py-4 px-1 border-b-2 font-medium text-sm ${
                                activeTab === tab.id
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            <span className="mr-2">{tab.icon}</span>
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Content */}
            <div>
                {activeTab === 'dashboard' && renderDashboard()}
                {activeTab === 'inputs' && renderInputs()}
                {activeTab === 'education' && renderEducation()}
            </div>
        </div>
    );
};

SMSFInvestmentLimitsMonitor.propTypes = {
    smsfState: PropTypes.object.isRequired,
    setSmsfState: PropTypes.func.isRequired
};

export default SMSFInvestmentLimitsMonitor;

